import { useEffect, useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import type {
  AppNotification,
  AuthFetch,
  Box,
  BoxDraft,
  NamedItem,
  Occurrence,
  OccurrenceDraft,
  Pop,
  Rompimento,
  RompimentoDraft,
  Session,
  User
} from './types';
import { API_BASE_URL, createAuthFetch } from './lib/api';
import { SESSION_KEY, csvRowToBox, normalizeRemoteBox } from './lib/boxes';
import type { LatLng } from './lib/geocode';
import RedeLogin from './components/RedeLogin';
import Topbar from './components/Topbar';
import Navbar, { type Tab } from './components/Navbar';
import NavigationPage from './components/navigation/NavigationPage';
import ReportsPage from './components/reports/ReportsPage';
import StockReportPage from './components/reports/StockReportPage';
import RoutesPage from './components/routes/RoutesPage';
import OccurrencesPage from './components/occurrences/OccurrencesPage';
import RompimentosPage from './components/rompimentos/RompimentosPage';
import SettingsPage from './components/settings/SettingsPage';
import './rede.css';

/**
 * Módulo Rede dentro do painel Farisx.
 *
 * Porte do sistema web original (mapa de ativação). O backend continua
 * externo e independente — ver lib/api.ts. A única diferença estrutural
 * pro original é que aqui não existe rota própria: as abas são estado
 * local, e a sessão do módulo é separada da sessão do painel.
 */
export default function RedeApp() {
  const [session, setSession] = useState<Session | null>(null);
  const [tab, setTab] = useState<Tab>('navigation');
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [pops, setPops] = useState<Pop[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [occurrences, setOccurrences] = useState<Occurrence[]>([]);
  const [cities, setCities] = useState<NamedItem[]>([]);
  const [occurrenceTypes, setOccurrenceTypes] = useState<NamedItem[]>([]);
  const [rompimentos, setRompimentos] = useState<Rompimento[]>([]);
  const [occurrencesLoading, setOccurrencesLoading] = useState(false);
  const [rompimentosLoading, setRompimentosLoading] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [boxesLoading, setBoxesLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [userLocation, setUserLocation] = useState<LatLng | null>(null);

  // Token sempre atual para o authFetch estável
  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = session;
  const authFetch = useMemo<AuthFetch>(() => createAuthFetch(() => sessionRef.current?.token), []);

  const popNames = useMemo(() => {
    const values = new Set(pops.map((p) => String(p.name || '').trim()).filter(Boolean));
    boxes.forEach((box) => {
      if (box.pop) values.add(String(box.pop).trim());
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [boxes, pops]);
  const uniquePops = useMemo(() => ['', ...popNames], [popNames]);

  /* ── carregamento ──────────────────────────────────────── */
  const loadBoxes = async (token?: string) => {
    setBoxesLoading(true);
    try {
      const data = await authFetch<unknown[]>('/boxes', { method: 'GET' }, token);
      setBoxes((Array.isArray(data) ? data : []).map((b, i) => normalizeRemoteBox(b, i)).filter(Boolean) as Box[]);
    } finally {
      setBoxesLoading(false);
    }
  };
  const loadPops = async (token?: string) => {
    const data = await authFetch<Pop[]>('/pops', { method: 'GET' }, token);
    setPops(Array.isArray(data) ? data : []);
  };
  const loadUsers = async (token?: string) => {
    const data = await authFetch<User[]>('/auth/users', { method: 'GET' }, token);
    setUsers(Array.isArray(data) ? data : []);
  };
  const loadOccurrences = async (token?: string) => {
    setOccurrencesLoading(true);
    try {
      const data = await authFetch<Occurrence[]>('/occurrences', { method: 'GET' }, token);
      setOccurrences(Array.isArray(data) ? data : []);
    } finally {
      setOccurrencesLoading(false);
    }
  };
  const loadCities = async (token?: string) => {
    const data = await authFetch<NamedItem[]>('/cities', { method: 'GET' }, token);
    setCities(Array.isArray(data) ? data : []);
  };
  const loadOccurrenceTypes = async (token?: string) => {
    const data = await authFetch<NamedItem[]>('/occurrence-types', { method: 'GET' }, token);
    setOccurrenceTypes(Array.isArray(data) ? data : []);
  };
  const loadRompimentos = async (token?: string) => {
    setRompimentosLoading(true);
    try {
      const data = await authFetch<Rompimento[]>('/rompimentos', { method: 'GET' }, token);
      setRompimentos(Array.isArray(data) ? data : []);
    } finally {
      setRompimentosLoading(false);
    }
  };

  /* ── restaura sessão ───────────────────────────────────── */
  useEffect(() => {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.token && parsed?.username) setSession(parsed);
    } catch {
      localStorage.removeItem(SESSION_KEY);
    }
  }, []);

  /* ── ao logar: carrega dados + geolocalização ──────────── */
  useEffect(() => {
    if (!session?.token) return;
    loadBoxes(session.token);
    loadPops(session.token);
    loadCities(session.token);
    loadOccurrenceTypes(session.token);
    loadOccurrences(session.token);
    loadRompimentos(session.token);
    if (session.type === 'admin') loadUsers(session.token);
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (p) => setUserLocation([p.coords.latitude, p.coords.longitude]),
      () => null,
      { enableHighAccuracy: true, maximumAge: 3000, timeout: 10000 }
    );
    return () => navigator.geolocation.clearWatch(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.token]);

  /* ── notificações (polling admin) ──────────────────────── */
  useEffect(() => {
    if (!session?.token || session.type !== 'admin') {
      setNotifications([]);
      return;
    }
    const token = session.token;
    const fetchNotifs = async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/notifications`, { headers: { Authorization: `Bearer ${token}` } });
        if (resp.ok) {
          const data = await resp.json().catch(() => []);
          setNotifications(Array.isArray(data) ? data : []);
        }
      } catch {
        /* ignora erros de rede */
      }
    };
    fetchNotifs();
    const intervalId = setInterval(fetchNotifs, 30000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.token]);

  const markNotifRead = async (id: string) => {
    try {
      await authFetch(`/notifications/${id}/read`, { method: 'PATCH' });
      setNotifications((prev) => prev.filter((n) => n._id !== id));
    } catch (err) {
      console.error('Erro ao marcar notificacao:', err);
    }
  };
  const markAllNotifRead = async () => {
    try {
      await authFetch('/notifications/read-all', { method: 'PATCH' });
      setNotifications([]);
    } catch (err) {
      console.error('Erro ao marcar notificacoes:', err);
    }
  };

  /* ── auth ──────────────────────────────────────────────── */
  const login = async ({ username, password }: { username: string; password: string }) => {
    setAuthLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Credenciais invalidas.');
      const next: Session = {
        token: data.token,
        userId: data.user?.id || '',
        username: data.user?.username || username,
        type: data.user?.type === 'admin' ? 'admin' : 'user'
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(next));
      setSession(next);
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
    setBoxes([]);
    setPops([]);
    setUsers([]);
    setOccurrences([]);
    setCities([]);
    setOccurrenceTypes([]);
    setRompimentos([]);
    setNotifications([]);
    setTab('navigation');
  };

  /* ── CRUD de caixas ────────────────────────────────────── */
  const saveBox = async (updated: BoxDraft) => {
    const originalId = updated.originalId || updated.id;
    const payload: Record<string, unknown> = { ...updated };
    delete payload.originalId;
    const saved = await authFetch(`/boxes/${encodeURIComponent(originalId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    setBoxes((prev) => prev.map((b) => (b.id === originalId ? normalizeRemoteBox(saved) || b : b)));
  };

  const createBox = async (payload: BoxDraft) => {
    if (!payload.id || !payload.name || !payload.type) {
      throw new Error('ID, Nome e Tipo são obrigatórios.');
    }
    const body = {
      id: String(payload.id).trim(),
      name: String(payload.name).trim(),
      type: payload.type,
      splitter: payload.splitter || '',
      pop: payload.pop || '',
      latitude: payload.latitude,
      longitude: payload.longitude,
      signal: payload.signal || '',
      status: payload.status || '',
      images: payload.images || [],
      servico: payload.servico || '',
      reason: 'Criação via mapa (Web)'
    };
    const saved = await authFetch('/boxes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const normalized = normalizeRemoteBox(saved);
    if (normalized) setBoxes((prev) => [...prev, normalized]);
  };

  const deleteBox = async (boxId: string) => {
    await authFetch(`/boxes/${encodeURIComponent(boxId)}`, { method: 'DELETE' });
    setBoxes((prev) => prev.filter((b) => b.id !== boxId));
  };

  const importCsv = async (file: File) => {
    if (!file) return;
    const parsed = Papa.parse<Record<string, any>>(await file.text(), { header: true, skipEmptyLines: true });
    const imported = (parsed.data || []).map(csvRowToBox).filter(Boolean) as Box[];
    if (imported.length === 0) {
      window.alert('Nenhuma caixa valida no CSV.');
      return;
    }
    const result = await authFetch<{ count?: number }>('/boxes/bulk-upsert', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ boxes: imported, reason: 'Importacao CSV (Web)' })
    });
    await loadBoxes();
    window.alert(`${result.count || imported.length} caixas processadas.`);
  };

  /* ── CRUD de ocorrências ───────────────────────────────── */
  const buildOccurrenceBody = (d: OccurrenceDraft) => ({
    contrato: d.contrato.trim(),
    nomeCliente: d.nomeCliente.trim(),
    endereco: d.endereco.trim(),
    bairro: d.bairro.trim(),
    cidade: d.cidade,
    tempoCliente: d.tempoCliente,
    primeiraOS: d.tempoCliente === 'ANTIGO' ? d.primeiraOS || null : null,
    board: d.board.trim(),
    port: d.port.trim(),
    serial: d.serial.trim(),
    ocorrencia: d.ocorrencia,
    localizacao: d.localizacao.trim(),
    status: d.status,
    tecnico: d.tecnico,
    situacao: d.situacao
  });

  const createOccurrence = async (draft: OccurrenceDraft) => {
    const saved = await authFetch<Occurrence>('/occurrences', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildOccurrenceBody(draft))
    });
    setOccurrences((prev) => [saved, ...prev]);
  };

  const updateOccurrence = async (id: string, draft: OccurrenceDraft) => {
    const saved = await authFetch<Occurrence>(`/occurrences/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildOccurrenceBody(draft))
    });
    setOccurrences((prev) => prev.map((o) => (o._id === id ? saved : o)));
  };

  const concludeOccurrence = async (id: string) => {
    const saved = await authFetch<Occurrence>(`/occurrences/${id}/conclude`, { method: 'PATCH' });
    setOccurrences((prev) => prev.map((o) => (o._id === id ? saved : o)));
  };

  const deleteOccurrence = async (id: string) => {
    await authFetch(`/occurrences/${id}`, { method: 'DELETE' });
    setOccurrences((prev) => prev.filter((o) => o._id !== id));
  };

  /* ── CRUD de rompimentos ───────────────────────────────── */
  const buildRompimentoBody = (d: RompimentoDraft) => ({
    titulo: d.titulo.trim(),
    inicio: new Date(d.inicio).toISOString(),
    previsao: d.previsao ? new Date(d.previsao).toISOString() : null,
    cidades: d.cidades,
    bairros: d.bairros.split(',').map((b) => b.trim()).filter(Boolean),
    descricao: d.descricao,
    mensagem: d.mensagem,
    areas: d.areas
  });

  const createRompimento = async (draft: RompimentoDraft) => {
    const saved = await authFetch<Rompimento>('/rompimentos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildRompimentoBody(draft))
    });
    setRompimentos((prev) => [saved, ...prev]);
  };

  const updateRompimento = async (id: string, draft: RompimentoDraft) => {
    const saved = await authFetch<Rompimento>(`/rompimentos/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildRompimentoBody(draft))
    });
    setRompimentos((prev) => prev.map((r) => (r._id === id ? saved : r)));
  };

  const finalizeRompimento = async (id: string) => {
    const saved = await authFetch<Rompimento>(`/rompimentos/${id}/finalize`, { method: 'PATCH' });
    setRompimentos((prev) => prev.map((r) => (r._id === id ? saved : r)));
  };

  const reopenRompimento = async (id: string) => {
    const saved = await authFetch<Rompimento>(`/rompimentos/${id}/reopen`, { method: 'PATCH' });
    setRompimentos((prev) => prev.map((r) => (r._id === id ? saved : r)));
  };

  const deleteRompimento = async (id: string) => {
    await authFetch(`/rompimentos/${id}`, { method: 'DELETE' });
    setRompimentos((prev) => prev.filter((r) => r._id !== id));
  };

  /* ── render ────────────────────────────────────────────── */
  if (!session) {
    return (
      <div className="rede-module flex h-full flex-col">
        <RedeLogin onLogin={login} loading={authLoading} />
      </div>
    );
  }

  const isAdmin = session.type === 'admin';
  const tabs: Tab[] = isAdmin
    ? ['navigation', 'occurrences', 'rompimentos', 'reports', 'stock', 'routes', 'settings']
    : ['navigation', 'routes'];

  return (
    <div className="rede-module flex h-full flex-col overflow-hidden bg-slate-50">
      <Topbar
        session={session}
        isAdmin={isAdmin}
        notifications={notifications}
        onMarkRead={markNotifRead}
        onMarkAllRead={markAllNotifRead}
        onLogout={logout}
      />
      <Navbar tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'navigation' && (
        <NavigationPage
          boxes={boxes}
          popNames={popNames}
          uniquePops={uniquePops}
          isAdmin={isAdmin}
          userLocation={userLocation}
          boxesLoading={boxesLoading}
          onSaveBox={saveBox}
          onCreateBox={createBox}
          onDeleteBox={deleteBox}
          onImportCsv={importCsv}
        />
      )}

      {tab === 'reports' && isAdmin && (
        <ReportsPage
          boxes={boxes}
          uniquePops={uniquePops}
          popNames={popNames}
          isAdmin={isAdmin}
          onSaveBox={saveBox}
          onDeleteBox={deleteBox}
        />
      )}

      {tab === 'stock' && isAdmin && <StockReportPage authFetch={authFetch} />}

      {tab === 'occurrences' && isAdmin && (
        <OccurrencesPage
          occurrences={occurrences}
          cities={cities}
          occurrenceTypes={occurrenceTypes}
          users={users}
          loading={occurrencesLoading}
          onCreate={createOccurrence}
          onUpdate={updateOccurrence}
          onConclude={concludeOccurrence}
          onDelete={deleteOccurrence}
          onReload={() => loadOccurrences()}
        />
      )}

      {tab === 'rompimentos' && isAdmin && (
        <RompimentosPage
          rompimentos={rompimentos}
          cities={cities}
          loading={rompimentosLoading}
          onCreate={createRompimento}
          onUpdate={updateRompimento}
          onFinalize={finalizeRompimento}
          onReopen={reopenRompimento}
          onDelete={deleteRompimento}
          onReload={() => loadRompimentos()}
        />
      )}

      {tab === 'routes' && <RoutesPage authFetch={authFetch} session={session} users={users} />}

      {tab === 'settings' && isAdmin && (
        <SettingsPage
          authFetch={authFetch}
          users={users}
          pops={pops}
          cities={cities}
          occurrenceTypes={occurrenceTypes}
          reloadUsers={loadUsers}
          reloadPops={loadPops}
          reloadCities={loadCities}
          reloadOccurrenceTypes={loadOccurrenceTypes}
        />
      )}
    </div>
  );
}
